/**
 * The single option-building path.
 *
 * Every tier funnels through here: tier-2 components build a `ChartConfig` and
 * delegate, tier 3 accepts one directly. Keeping this pure (and separate from
 * `ChartSurface`) is what lets the builders be unit-tested in the node project
 * without a browser or the echarts runtime.
 */
import { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import type { ChartMedia, ChartMode } from '@/tokens/chart';
import type { ChartConfig, ChartRow, ChartType } from './types';
import { baseOption } from './buildOption/shared';
import { buildCartesianOption } from './buildOption/cartesian';
import { buildPartToWholeOption } from './buildOption/partToWhole';
import { buildMatrixOption } from './buildOption/matrix';
import { buildFlowOption } from './buildOption/flow';

type Family = 'cartesian' | 'partToWhole' | 'matrix' | 'flow' | 'none';

/**
 * A mapped type over ChartType keeps this exhaustive: adding a chart type is a
 * compile error until it is routed here.
 */
const FAMILY: { [K in ChartType]: Family } = {
  line: 'cartesian',
  area: 'cartesian',
  bar: 'cartesian',
  stackedBar: 'cartesian',
  horizontalBar: 'cartesian',
  scatter: 'cartesian',
  pie: 'partToWhole',
  donut: 'partToWhole',
  heatmap: 'matrix',
  sankey: 'flow',
  // Rendered by StatTile and the Table organism respectively — not ECharts
  // instances, so they produce an empty option here.
  kpi: 'none',
  table: 'none',
};

export function buildChartOption(
  config: ChartConfig,
  data: readonly ChartRow[],
  mode: ChartMode,
  media: ChartMedia,
): EChartsOption {
  switch (FAMILY[config.chartType]) {
    case 'cartesian':
      return buildCartesianOption(config, data, mode, media);
    case 'partToWhole':
      return buildPartToWholeOption(config, data, mode, media);
    case 'matrix':
      return buildMatrixOption(config, data, mode, media);
    case 'flow':
      return buildFlowOption(config, data, mode, media);
    case 'none':
    default:
      return { ...baseOption(config, mode, media), series: [] } as EChartsOption;
  }
}

export function useChartOption(
  config: ChartConfig,
  data: readonly ChartRow[],
  mode: ChartMode,
  media: ChartMedia,
): EChartsOption {
  return useMemo(() => buildChartOption(config, data, mode, media), [config, data, mode, media]);
}
